"use client";

import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import LinkButton from "@/components/ui/LinkButton";

interface HardwareCardProps {
  children?: ReactNode;
  imageSrc: string;
  imageAlt?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  linkText?: string;
  linkUrl?: string;
  className?: string;
}

export default function HardwareCard({
  children,
  imageSrc,
  imageAlt = "Hardware",
  title,
  subtitle,
  description,
  linkText,
  linkUrl,
  className = "",
}: HardwareCardProps) {
  // Wenn nur die URL da ist, wird die ganze Karte ein Link
  const hasLink = Boolean(linkUrl);
  // Wenn Text UND URL da sind, rendern wir den Button
  const hasButton = Boolean(linkText && linkUrl);

  const CardInner = (
    <>
      {/* Image */}
      <div className="relative w-full bg-slate-50">
        <div className="relative h-64 w-full sm:h-72">
          <Image
            src={imageSrc}
            alt={imageAlt}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            className="object-contain p-3 sm:p-4 transition-transform duration-300 group-hover:scale-[1.04]"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-3 p-6 text-center">
        {title && <h3 className="text-xl font-bold text-slate-900">{title}</h3>}

        {subtitle && (
          <p className="text-base font-semibold text-slate-900">{subtitle}</p>
        )}

        {description && (
          <p className="text-base text-slate-600">{description}</p>
        )}

        {children}
      </div>

      {/* CTA Bereich */}
      {hasButton ? (
        <div className="flex justify-center px-6 pb-6">
          <div className="w-full max-w-65">
            <LinkButton href={linkUrl!} variant="primary" className="w-full">
              {linkText!}
            </LinkButton>
          </div>
        </div>
      ) : hasLink ? (
        <div className="flex justify-center px-6 pb-6">
          <span className="inline-flex items-center justify-center rounded-2xl bg-brand-primary hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl px-6 py-2 text-sm font-semibold text-white shadow transition-all">
            Angebot anfragen
          </span>
        </div>
      ) : null}
    </>
  );

  const cardClasses = [
    "group flex h-full flex-col overflow-hidden rounded-2xl",
    "bg-white shadow-lg transition-all duration-300",
    "hover:-translate-y-1 hover:shadow-xl",
    className,
  ].join(" ");

  // Wenn ein Link ohne Button-Text existiert, Karte als <Link> rendern
  if (hasLink && !hasButton) {
    return (
      <Link href={linkUrl!} className={cardClasses}>
        {CardInner}
      </Link>
    );
  }

  // Ansonsten als normales <div> (Button regelt Link oder gar kein Link)
  return <div className={cardClasses}>{CardInner}</div>;
}
